import * as React from 'react';
import {Text} from 'components/UIKit';
import {trackTealium} from "utilities/analytics";
import {Shop, Tag, ListingShop, ListingMetric, ShopCategory} from './types';

export async function trackPageImageClick(shopTitle: string) {
  await trackTealium('store_page_image_click', 'store_link', shopTitle);
}

export interface MetricSchema {
  title: string;
  testFn?: (value: string | number) => boolean;
  shopKey: string;
  currency: string | boolean;
  verified?: boolean;
  warning?: (shop: Shop) => boolean;
  percentage?: boolean;
}

function getMetricSchema(condensed: boolean, i18n): MetricSchema[] {
  const monthly = <Text secondary text={` /${i18n.translate('month')}`} />;
  return [
    {
      title: i18n.translate(condensed ? 'smallAvgRevMonthly' : 'avgRevMonthly', {period: monthly}),
      shopKey: 'averageMonthlyRevenue',
      currency: 'USD',
      verified: true,
    },
    {
      title: i18n.translate(condensed ? 'smallAvgSessionsMonthly' : 'avgSessionsMonthly', {
        period: monthly,
      }),
      shopKey: 'averageMonthlyTraffic',
      currency: false,
      verified: true,
    },
    {
      title: i18n.translate(condensed ? 'smallAvgProfitMonthly' : 'avgProfitMonthly', {
        period: monthly,
      }),
      shopKey: 'averageProfitPerMonth',
      currency: 'USD',
      warning: (shop) => Boolean(shop.profitDetails),
    },
    {
      title: i18n.translate(condensed ? 'smallInventoryValue' : 'inventoryValue'),
      shopKey: 'inventoryValue',
      testFn: (value) => value > 0,
      currency: 'USD',
    },
  ];
}

export function getTrafficAndPerfMetrics(i18n): MetricSchema[] {
  const monthly = <Text secondary text={` /${i18n.translate('month')}`} />;
  return [
    ...getMetricSchema(false, i18n),
    {
      title: i18n.translate('profitMargin'),
      shopKey: 'overallProfitMargins',
      percentage: true,
      currency: false,
    },
    {
      title: i18n.translate('avgSalesMonthly', {period: monthly}),
      shopKey: 'averageNumberMonthlySales',
      currency: false,
    },
  ];
}

export function getShopMetrics(shop: Shop, schema: MetricSchema[]): ListingMetric[] {
  return schema
    .filter(({testFn, shopKey}) => {
      if (testFn) {
        return testFn(shop[shopKey]);
      }
      return true;
    })
    .map(({title, shopKey, currency, percentage, verified = false, warning = false}) => ({
      title,
      verified,
      currency,
      percentage,
      value: shop[shopKey],
      warning: warning ? warning(shop) : false,
    }));
}

function categoryToTag(category: ShopCategory): Tag {
  const {url, title} = category;
  return {
    link: url,
    tag: title,
  };
}

export function getTags(shop: Shop): Tag[] {
  const {categories = []} = shop;
  return categories.map(categoryToTag);
}

export function getShortTags(shop: Shop): Tag[] {
  const {industry, businessTypes} = shop;
  return [industry, ...businessTypes].filter(Boolean).map(categoryToTag);
}

export function getListingShopData(shop, i18n): ListingShop {
  return {
    ...shop,
    tags: getTags(shop),
    metrics: getShopMetrics(shop, getMetricSchema(false, i18n)),
    shortTags: getShortTags(shop),
    condensedMetrics: getShopMetrics(shop, getMetricSchema(true, i18n)),
    trafficAndPerformance: getShopMetrics(shop, getTrafficAndPerfMetrics(i18n)),
  };
}
